import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { SwPush } from "@angular/service-worker";
import { distinctUntilChanged, filter, firstValueFrom, map, Observable, of, race, switchMap, take, timer } from "rxjs";
import { environment } from "src/environments/environment";
import { TOKEN } from "../const/constant";
import { AuthenticationService } from "./auth.service";
import { LocalStorageService } from "./local-storage.service";

export interface PushTestResult {
	sent: number;
	removed: number;
	failed: number;
}

/** Le logout n'attend pas plus longtemps le désabonnement : il ne doit jamais rester bloqué. */
const LOGOUT_TIMEOUT_MS = 3000;

/**
 * Notifications push (Web Push + clés VAPID, sans Firebase) via le service worker Angular.
 *
 * - la permission n'est demandée que depuis un clic (subscribe), jamais au chargement : obligatoire sur iPhone ;
 * - sur iPhone / iPad (iOS 16.4+), uniquement quand l'application est installée sur l'écran d'accueil ;
 * - l'abonnement est rattaché à l'utilisateur connecté, et retiré au logout (un autre utilisateur peut se
 *   connecter sur le même appareil).
 */
@Injectable({ providedIn: "root" })
export class PushService {
	private apiURL = environment.apiURL;
	private started = false;
	private publicKey: string | null = null;

	/** Abonnement de ce navigateur (null si aucun). */
	readonly subscription$: Observable<PushSubscription | null>;

	constructor(
		private swPush: SwPush,
		private http: HttpClient,
		private router: Router,
		private authenticationService: AuthenticationService,
		private localStorageService: LocalStorageService
	) {
		this.subscription$ = this.swPush.isEnabled ? this.swPush.subscription : of(null);
	}

	isSupported(): boolean {
		return this.swPush.isEnabled && "Notification" in window && "PushManager" in window;
	}

	permission(): NotificationPermission {
		return "Notification" in window ? Notification.permission : "denied";
	}

	isIOS(): boolean {
		return /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
	}

	isInstalled(): boolean {
		return window.matchMedia("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
	}

	/**
	 * À appeler une fois au démarrage de l'application :
	 * - au login, rattache l'abonnement existant de ce navigateur au nouvel utilisateur ;
	 * - clic sur une notification alors que l'application est ouverte : navigation vers sa page.
	 */
	start(): void {
		if (this.started || !this.isSupported()) return;
		this.started = true;

		this.authenticationService.currentUser$
			.pipe(
				map((user) => user?.id ?? null),
				distinctUntilChanged(),
				filter((id): id is number => id !== null),
				// Au login, le jeton n'est envoyé qu'une fois la navigation passée par le guard : on attend la fin
				// de la navigation suivante (ou 3 s au plus).
				switchMap(() => race(this.router.events.pipe(filter((e) => e instanceof NavigationEnd)), timer(LOGOUT_TIMEOUT_MS)).pipe(take(1)))
			)
			.subscribe(() => this.resync());

		this.swPush.notificationClicks.subscribe(({ notification }) => {
			const url = notification?.data?.url;
			if (typeof url === "string" && url.startsWith("/")) this.router.navigateByUrl(url);
		});
	}

	/**
	 * Active les notifications sur cet appareil. À appeler directement depuis un clic : la demande de
	 * permission est faite avant tout appel asynchrone (sinon iOS la refuse).
	 * Rejette avec un message à afficher en cas d'échec.
	 */
	async subscribe(): Promise<void> {
		if (!this.isSupported()) throw new Error("Les notifications ne sont pas disponibles sur ce navigateur.");

		const permission = await Notification.requestPermission();
		if (permission !== "granted") throw new Error("Notifications bloquées. Autorisez-les dans les réglages du navigateur.");

		const serverPublicKey = await this.loadPublicKey();
		if (!serverPublicKey) throw new Error("Les notifications ne sont pas encore configurées sur le serveur.");

		let subscription: PushSubscription;
		try {
			subscription = await this.swPush.requestSubscription({ serverPublicKey });
		} catch (error) {
			console.warn("[push] abonnement refusé par le navigateur", error);
			throw new Error("Le navigateur a refusé l'abonnement aux notifications.");
		}

		try {
			await this.register(subscription);
		} catch (error) {
			// Sans enregistrement côté serveur, l'abonnement ne recevrait rien : on le retire pour rester cohérent
			await this.swPush.unsubscribe().catch(() => undefined);
			throw new Error("Impossible d'enregistrer l'abonnement. Réessayez plus tard.");
		}
	}

	/** Désactive les notifications sur cet appareil. Ne rejette jamais (appelée aussi au logout). */
	async unsubscribe(): Promise<void> {
		if (!this.swPush.isEnabled) return;
		try {
			const subscription = await firstValueFrom(this.swPush.subscription);
			if (!subscription) return;
			if (this.hasToken()) {
				await firstValueFrom(this.http.post(`${this.apiURL}/push/unsubscribe`, { data: { endpoint: subscription.endpoint } })).catch((error) =>
					console.warn("[push] désabonnement côté serveur impossible", error)
				);
			}
			await this.swPush.unsubscribe();
		} catch (error) {
			console.warn("[push] désabonnement impossible", error);
		}
	}

	/** Désabonnement avant logout, limité dans le temps : le logout ne doit pas attendre un réseau lent. */
	unsubscribeBeforeLogout(): Promise<void> {
		return Promise.race([this.unsubscribe(), new Promise<void>((resolve) => setTimeout(resolve, LOGOUT_TIMEOUT_MS))]);
	}

	async sendTest(): Promise<PushTestResult> {
		// Le navigateur peut se croire abonné alors que le serveur ne le connaît plus (désabonnement interrompu,
		// base réinitialisée) : on lui renvoie d'abord l'abonnement courant
		await this.resync();
		return firstValueFrom(this.http.post<{ data: PushTestResult }>(`${this.apiURL}/push/test`, {})).then((res) => res.data);
	}

	private async loadPublicKey(): Promise<string | null> {
		if (this.publicKey) return this.publicKey;
		const res = await firstValueFrom(this.http.get<{ data: { publicKey: string | null } }>(`${this.apiURL}/push/public-key`));
		this.publicKey = res?.data?.publicKey || null;
		return this.publicKey;
	}

	private register(subscription: PushSubscription): Promise<unknown> {
		return firstValueFrom(this.http.post(`${this.apiURL}/push/subscribe`, { data: subscription.toJSON() }));
	}

	/** Renvoie au serveur l'abonnement de ce navigateur, s'il en a un. Ne rejette jamais, n'affiche rien. */
	async resync(): Promise<void> {
		try {
			if (this.permission() !== "granted" || !this.hasToken()) return;
			const subscription = await firstValueFrom(this.swPush.subscription);
			if (subscription) await this.register(subscription);
		} catch (error) {
			console.warn("[push] synchronisation de l'abonnement impossible", error);
		}
	}

	private hasToken(): boolean {
		try {
			return !!this.localStorageService.get(TOKEN);
		} catch {
			return false;
		}
	}
}
